import { useNavigate } from 'react-router-dom';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { motion } from 'framer-motion';
import { Activity, Brain, Sparkles, Lock, ChevronRight, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface MiniAIWidgetsProps {
    healthScore?: number;
    predictiveStatus?: 'on_track' | 'at_risk' | 'delayed';
    isLoading?: boolean;
}

export function MiniAIWidgets({
    healthScore = 0,
    predictiveStatus = 'on_track',
    isLoading = false
}: MiniAIWidgetsProps) {
    const navigate = useNavigate();
    const { plan, isAdmin } = usePlanLimits();
    const isProfessional = plan === 'profissional' || isAdmin;

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'on_track':
                return { label: 'No prazo', color: 'bg-emerald-500', textColor: 'text-emerald-600' };
            case 'at_risk':
                return { label: 'Em risco', color: 'bg-amber-500', textColor: 'text-amber-600' };
            case 'delayed':
                return { label: 'Atrasado', color: 'bg-red-500', textColor: 'text-red-600' };
            default:
                return { label: 'Analisando', color: 'bg-muted', textColor: 'text-muted-foreground' };
        }
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-amber-500';
        return 'text-red-500';
    };

    const statusConfig = getStatusConfig(predictiveStatus);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    Insights de IA
                </h2>
                <button
                    onClick={() => navigate('/ia')}
                    className="text-xs text-primary flex items-center gap-1 hover:underline"
                >
                    Ver todas
                    <ChevronRight className="h-3 w-3" />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {/* Mini Health Score */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    onClick={() => navigate('/ia')}
                    className={cn(
                        "p-3 rounded-xl bg-card/50 border border-border/30 cursor-pointer",
                        "active:scale-[0.98] transition-transform",
                        "hover:border-primary/30"
                    )}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <Activity className="h-4 w-4 text-primary" />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 cursor-help">
                                        Saúde
                                        <HelpCircle className="h-3 w-3" />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[200px]">
                                    <p className="text-xs">Score calculado com margem de lucro, velocidade de tarefas e progresso dos projetos.</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        {!isProfessional && (
                            <Lock className="h-3 w-3 text-amber-500" />
                        )}
                    </div>

                    {isLoading ? (
                        <div className="h-8 bg-muted/50 rounded animate-pulse" />
                    ) : isProfessional ? (
                        <>
                            <div className={cn("text-2xl font-bold", getScoreColor(healthScore))}>
                                {healthScore}
                            </div>
                            <p className="text-[10px] text-muted-foreground">de 100 pontos</p>
                        </>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            <span className="text-primary text-xs">Premium</span>
                        </div>
                    )}
                </motion.div>

                {/* Mini Predictive */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    onClick={() => navigate('/ia')}
                    className={cn(
                        "p-3 rounded-xl bg-card/50 border border-border/30 cursor-pointer",
                        "active:scale-[0.98] transition-transform",
                        "hover:border-primary/30"
                    )}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <Brain className="h-4 w-4 text-violet-500" />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 cursor-help">
                                        Previsão
                                        <HelpCircle className="h-3 w-3" />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[200px]">
                                    <p className="text-xs">A IA analisa tarefas e prazos para prever se seus projetos vão atrasar.</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        {!isProfessional && (
                            <Lock className="h-3 w-3 text-amber-500" />
                        )}
                    </div>

                    {isLoading ? (
                        <div className="h-6 bg-muted/50 rounded animate-pulse" />
                    ) : isProfessional ? (
                        <Badge
                            className={cn(
                                "text-[10px] px-2 py-0.5",
                                statusConfig.color,
                                "text-white"
                            )}
                        >
                            {statusConfig.label}
                        </Badge>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            <span className="text-primary text-xs">Premium</span>
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Quick AI Action */}
            <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={() => navigate('/agente-ia')}
                className={cn(
                    "w-full p-3 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10",
                    "border border-violet-500/20 hover:border-violet-500/40",
                    "flex items-center justify-between",
                    "active:scale-[0.98] transition-all"
                )}
            >
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                        <Sparkles className="h-4 w-4 text-violet-500" />
                    </div>
                    <div className="text-left">
                        <p className="text-xs font-medium">Pergunte à IA</p>
                        <p className="text-[10px] text-muted-foreground">Sobre seus projetos</p>
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 text-violet-500" />
            </motion.button>
        </div>
    );
}
